import React, { useState, useEffect, useCallback, useRef } from "react";
import { Spin } from "antd";
import * as recruitmentApi from "../../../../../core/data/redux/services/recruitmentApi";
import { notify } from "../../../../../services/toast.config";

interface EducationEntry {
  degree: string;
  institution: string;
  year: string;
  field?: string;
}
interface WorkExperienceEntry {
  company: string;
  designation: string;
  from_year: string;
  to_year: string;
  is_current: boolean;
  description?: string;
}
interface LanguageEntry {
  language: string;
  proficiency: string;
}
interface ProfileTabProps {
  primaryColor?: string;
}

const EMPLOYMENT_STATUSES = [
  { value: "employed", label: "Employed" },
  { value: "unemployed", label: "Unemployed" },
  { value: "freelancing", label: "Freelancing" },
  { value: "student", label: "Student" },
  { value: "notice_period", label: "Notice Period" },
];
const WORK_MODES = [
  { value: "remote", label: "Remote" },
  { value: "onsite", label: "On-site" },
  { value: "hybrid", label: "Hybrid" },
  { value: "any", label: "Any" },
];
const PROFICIENCY_LEVELS = ["Basic", "Conversational", "Professional", "Native"];

const emptyEdu = (): EducationEntry => ({ degree: "", institution: "", year: "", field: "" });
const emptyExp = (): WorkExperienceEntry => ({ company: "", designation: "", from_year: "", to_year: "", is_current: false, description: "" });
const emptyLang = (): LanguageEntry => ({ language: "", proficiency: "Conversational" });


const T = {
  radius: { card: 14, input: 9, badge: 20, icon: 9 },
  shadow: { card: "0 1px 4px rgba(0,0,0,0.06)", input: "0 1px 2px rgba(0,0,0,0.04)" },
  font: { label: 11, body: 13.5, heading: 15 },
  space: { sectionGap: 14, fieldGap: 18 },
  neutral: { 50: "#fafafa", 100: "#f4f5f7", 200: "#e8eaed", 300: "#d1d5db", 400: "#9ca3af", 600: "#6b7280", 800: "#1f2937", 900: "#111827" },
};


const SectionCard: React.FC<{
  icon: string; title: string; subtitle?: string;
  rightSlot?: React.ReactNode; children: React.ReactNode; accent: string;
}> = ({ icon, title, subtitle, rightSlot, children, accent }) => (
  <div style={{ background: "#fff", border: `1px solid ${T.neutral[200]}`, borderRadius: T.radius.card, overflow: "hidden", boxShadow: T.shadow.card }}>
    <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.neutral[100]}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: T.neutral[50] }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
        <span style={{ width: 34, height: 34, borderRadius: T.radius.icon, background: `${accent}14`, display: "flex", alignItems: "center", justifyContent: "center", color: accent, fontSize: 16, flexShrink: 0 }}>
          <i className={`ti ${icon}`} />
        </span>
        <div>
          <div style={{ fontWeight: 650, fontSize: T.font.heading, color: T.neutral[900], lineHeight: 1.3 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: T.neutral[400], marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
      {rightSlot}
    </div>
    <div style={{ padding: "20px 20px" }}>{children}</div>
  </div>
);

const Lbl: React.FC<{ children: React.ReactNode; required?: boolean; editing?: boolean }> = ({ children, required, editing }) => (
  <div style={{ fontSize: T.font.label, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.neutral[400], marginBottom: 7, display: "flex", alignItems: "center", gap: 3 }}>
    {children}{required && editing && <span style={{ color: "#ef4444" }}>*</span>}
  </div>
);

const Val: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: T.font.body, fontWeight: 500, color: children ? T.neutral[800] : T.neutral[300], lineHeight: 1.5, minHeight: 22 }}>
    {children || "—"}
  </div>
);

const Inp = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { accent?: string }>(
  ({ accent = "#4f46e5", ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    return (
      <input ref={ref} {...props}
        style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${focused ? accent : T.neutral[200]}`, borderRadius: T.radius.input, fontSize: T.font.body, color: T.neutral[900], background: "#fff", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s", boxShadow: focused ? `0 0 0 3px ${accent}18` : T.shadow.input, ...props.style }}
        onFocus={e => { setFocused(true); props.onFocus?.(e); }}
        onBlur={e => { setFocused(false); props.onBlur?.(e); }}
      />
    );
  }
);

const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { accent?: string }> = ({ children, accent = "#4f46e5", ...p }) => (
  <select {...p} style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${T.neutral[200]}`, borderRadius: T.radius.input, fontSize: T.font.body, color: T.neutral[900], background: "#fff", outline: "none", cursor: "pointer", boxSizing: "border-box", boxShadow: T.shadow.input, ...p.style }}>
    {children}
  </select>
);

const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement> & { accent?: string }> = ({ accent = "#4f46e5", ...p }) => {
  const [focused, setFocused] = useState(false);
  return (
    <textarea {...p} style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${focused ? accent : T.neutral[200]}`, borderRadius: T.radius.input, fontSize: T.font.body, color: T.neutral[900], background: "#fff", outline: "none", resize: "vertical", boxSizing: "border-box", transition: "border-color 0.15s", boxShadow: focused ? `0 0 0 3px ${accent}18` : T.shadow.input, minHeight: 72, ...p.style }}
      onFocus={e => { setFocused(true); p.onFocus?.(e); }}
      onBlur={e => { setFocused(false); p.onBlur?.(e); }}
    />
  );
};

const Grid: React.FC<{ children: React.ReactNode; cols?: number }> = ({ children, cols = 2 }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: T.space.fieldGap }}>{children}</div>
);

const SmBtn: React.FC<{ onClick: () => void; children: React.ReactNode; color: string; variant?: "outline" | "ghost" }> = ({ onClick, children, color, variant = "outline" }) => (
  <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 13px", background: variant === "ghost" ? "transparent" : `${color}10`, color, border: `1px solid ${variant === "ghost" ? "transparent" : `${color}28`}`, borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
    {children}
  </button>
);

const CompletionRing: React.FC<{ pct: number }> = ({ pct }) => {
  const r = 30, circ = 2 * Math.PI * r;
  const color = pct >= 80 ? "#22c55e" : pct >= 50 ? "#f59e0b" : "#ef4444";
  const label = pct >= 80 ? "Great!" : pct >= 50 ? "Good" : "Incomplete";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      <div style={{ position: "relative", width: 76, height: 76 }}>
        <svg width="76" height="76" style={{ transform: "rotate(-90deg)", position: "absolute" }}>
          <circle cx="38" cy="38" r={r} fill="none" stroke={T.neutral[100]} strokeWidth="5.5" />
          <circle cx="38" cy="38" r={r} fill="none" stroke={color} strokeWidth="5.5" strokeDasharray={circ} strokeDashoffset={circ - (pct / 100) * circ} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.7s cubic-bezier(.4,0,.2,1)" }} />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 750, color, lineHeight: 1 }}>{pct}%</span>
        </div>
      </div>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: T.neutral[400] }}>{label}</div>
    </div>
  );
};

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; accent: string }> = ({ checked, onChange, label, accent }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none", width: "fit-content" }} onClick={() => onChange(!checked)}>
    <div style={{ width: 40, height: 22, borderRadius: 11, background: checked ? accent : T.neutral[200], position: "relative", transition: "background 0.2s", flexShrink: 0, boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)" }}>
      <div style={{ position: "absolute", width: 16, height: 16, borderRadius: "50%", background: "#fff", top: 3, left: checked ? 21 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.25)" }} />
    </div>
    <span style={{ fontSize: T.font.body, fontWeight: 500, color: T.neutral[800] }}>{label}</span>
  </div>
);

const Divider = () => <div style={{ height: 1, background: T.neutral[100], margin: "16px 0" }} />;

const fmtFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};


const ProfileTab: React.FC<ProfileTabProps> = ({ primaryColor = "#4f46e5" }) => {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [completion, setCompletion] = useState(0);


  const [resumes, setResumes] = useState<any[]>([]);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [resumeDragOver, setResumeDragOver] = useState(false);
  const resumeInputRef = useRef<HTMLInputElement>(null);


  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const [uploadingPic, setUploadingPic] = useState(false);
  const picInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    full_name: "", phone: "", alternate_phone: "",
    preferred_contact_method: "email",
    current_location: "", current_city: "",
    total_experience_years: 0 as number | string,
    current_employment_status: "", current_company: "", current_designation: "",
    notice_period_days: "" as number | string,
    current_ctc: "" as number | string,
    expected_ctc: "" as number | string,
    linkedin_profile: "",
    willing_to_relocate: false,
    preferred_work_mode: "any",
    highest_education: "",
  });

  const [keySkills, setKeySkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState("");
  const [education, setEducation] = useState<EducationEntry[]>([emptyEdu()]);
  const [workExperience, setWorkExperience] = useState<WorkExperienceEntry[]>([]);
  const [languages, setLanguages] = useState<LanguageEntry[]>([]);
  const [portfolioLinks, setPortfolioLinks] = useState<string[]>([""]);


  const populate = useCallback((data: any) => {
    setForm({
      full_name: data.full_name || "",
      phone: data.phone || "",
      alternate_phone: data.alternate_phone || "",
      preferred_contact_method: data.preferred_contact_method || "email",
      current_location: data.current_location || "",
      current_city: data.current_city || "",
      total_experience_years: data.total_experience_years || 0,
      current_employment_status: data.current_employment_status || "",
      current_company: data.current_company || "",
      current_designation: data.current_designation || "",
      notice_period_days: data.notice_period_days || "",
      current_ctc: data.current_ctc || "",
      expected_ctc: data.expected_ctc || "",
      linkedin_profile: data.linkedin_profile || "",
      willing_to_relocate: data.willing_to_relocate || false,
      preferred_work_mode: data.preferred_work_mode || "any",
      highest_education: data.highest_education || "",
    });
    setKeySkills(
      Array.isArray(data.key_skills) ? data.key_skills
        : typeof data.key_skills === "string" ? data.key_skills.split(",").map((s: string) => s.trim()).filter(Boolean)
          : []
    );
    setEducation(Array.isArray(data.education_history) && data.education_history.length > 0 ? data.education_history : [emptyEdu()]);
    setWorkExperience(Array.isArray(data.work_experience) && data.work_experience.length > 0 ? data.work_experience : []);
    setLanguages(Array.isArray(data.languages) && data.languages.length > 0 ? data.languages : []);
    setPortfolioLinks(Array.isArray(data.portfolio_links) && data.portfolio_links.length > 0 ? data.portfolio_links : [""]);
    setResumes(Array.isArray(data.resumes) ? data.resumes : []);
    setCompletion(data.profile_completion_percentage || 0);
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const companyId = localStorage.getItem("company_id") || "";
      const companyType = localStorage.getItem("company_type") || "";
      const data = await recruitmentApi.getCandidateProfile(companyId, companyType);
      setProfile(data);
      populate(data);

      if (data.profile_pic_serve_url) {
        setProfilePicUrl(data.profile_pic_serve_url);
      }

    } catch { } finally { setLoading(false); }
  }, [populate]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const hc = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm(p => ({ ...p, [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value }));
  };

  const addSkill = () => {
    const t = skillInput.trim();
    if (t && !keySkills.includes(t)) setKeySkills(p => [...p, t]);
    setSkillInput("");
  };


  const handleProfilePicChange = async (file: File) => {
    if (!file.type.startsWith("image/")) return notify.error("Please select an image file");
    if (file.size > 2 * 1024 * 1024) return notify.error("Image must be under 2 MB");

    setUploadingPic(true);
    try {
      const companyId = localStorage.getItem("company_id") || "";
      const companyType = localStorage.getItem("company_type") || "";
      const formData = new FormData();
      formData.append("profile_pic", file);
      formData.append("company_id", companyId);
      formData.append("company_type", companyType);

      const result = await (recruitmentApi as any).uploadProfilePic(formData);

      setProfilePicUrl(result.serve_url);
      notify.success("Profile picture updated!");
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.data?.message || err?.message || "Failed to Upload Profile Picture");
    } finally {
      setUploadingPic(false);
    }
  };


  const handleResumeUpload = async (file: File) => {
    const allowed = ["application/pdf", "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/rtf", "text/plain"];
    if (!allowed.includes(file.type)) return notify.error("Allowed types: PDF, DOC, DOCX, RTF, TXT");
    if (file.size > 5 * 1024 * 1024) return notify.error("File must be under 5 MB");

    setUploadingResume(true);
    try {
      const companyId = localStorage.getItem("company_id") || "";
      const companyType = localStorage.getItem("company_type") || "";
      const formData = new FormData();
      formData.append("resume", file);
      formData.append("company_id", companyId);
      formData.append("company_type", companyType);
      formData.append("is_primary", "true");


      const response = await (recruitmentApi as any).uploadResume(formData);
      notify.success("Resume uploaded successfully!");
      fetchProfile();
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.data?.message || err?.message || "Failed to Upload Resume");
    } finally {
      setUploadingResume(false);
    }
  };

  const handleResumeDrop = (e: React.DragEvent) => {
    e.preventDefault(); setResumeDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleResumeUpload(file);
  };


  const handleSave = async () => {
    if (!form.full_name.trim()) return notify.error("Full name is required");
    if (!form.phone.trim()) return notify.error("Phone number is required");
    if (!form.current_employment_status) return notify.error("Employment status is required");
    if (keySkills.length === 0) return notify.error("Add at least one key skill");
    const validEdu = education.filter(e => e.degree.trim() && e.institution.trim());
    if (validEdu.length === 0) return notify.error("Add at least one education entry");

    setSaving(true);
    try {
      const companyId = localStorage.getItem("company_id") || "";
      const companyType = localStorage.getItem("company_type") || "";
      await recruitmentApi.createCandidateProfile(companyId, companyType, {
        ...form,
        key_skills: keySkills,
        education_history: validEdu,
        work_experience: workExperience.filter(e => e.company.trim() && e.designation.trim()),
        languages: languages.filter(l => l.language.trim()),
        portfolio_links: portfolioLinks.filter(l => l.trim()),
        notice_period_days: form.notice_period_days ? Number(form.notice_period_days) : undefined,
        current_ctc: form.current_ctc ? Number(form.current_ctc) : undefined,
        expected_ctc: form.expected_ctc ? Number(form.expected_ctc) : undefined,
        total_experience_years: Number(form.total_experience_years),
      });
      notify.success("Profile saved successfully!");
      setEditing(false);
      fetchProfile();
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.data?.message || err?.message || "Failed to save profile");
    } finally { setSaving(false); }
  };

  const cancelEdit = () => { setEditing(false); if (profile) populate(profile); };

  const initials = form.full_name
    ? form.full_name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  if (loading) return (
    <div style={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spin size="large" />
    </div>
  );


  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space.sectionGap }}>


      <input ref={picInputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleProfilePicChange(f); e.target.value = ""; }} />
      <input ref={resumeInputRef} type="file" accept=".pdf,.doc,.docx,.rtf,.txt" style={{ display: "none" }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleResumeUpload(f); e.target.value = ""; }} />


      <div style={{ background: `linear-gradient(135deg, ${primaryColor}0a 0%, #fff 60%)`, border: `1px solid ${T.neutral[200]}`, borderRadius: T.radius.card, padding: "20px 24px", boxShadow: T.shadow.card }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 220 }}>


            <div style={{ position: "relative", flexShrink: 0 }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", overflow: "hidden", background: profilePicUrl ? "transparent" : `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20, fontWeight: 700, boxShadow: `0 3px 12px ${primaryColor}38`, border: `3px solid #fff` }}>
                {profilePicUrl
                  ? <img
                    src={profilePicUrl}
                    alt="Profile"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={() => setProfilePicUrl(null)}
                  />
                  : initials
                }
              </div>

              <button
                onClick={() => !uploadingPic && picInputRef.current?.click()}
                title="Change profile picture"
                style={{
                  position: "absolute", bottom: -2, right: -2,
                  width: 24, height: 24, borderRadius: "50%",
                  background: uploadingPic ? "#9ca3af" : primaryColor,
                  border: "2px solid #fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: uploadingPic ? "not-allowed" : "pointer",
                  color: "#fff", fontSize: 11,
                  transition: "background 0.15s",
                }}
              >
                {uploadingPic
                  ? <span style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                  : <i className="ti ti-camera" />
                }
              </button>
            </div>

            <div>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: T.neutral[900], margin: 0, lineHeight: 1.3 }}>
                {form.full_name || "Your Name"}
              </h2>
              <div style={{ fontSize: 13, color: T.neutral[600], marginTop: 3, lineHeight: 1.4 }}>
                {profile?.email}
                {form.current_designation && form.current_company && (
                  <span style={{ marginLeft: 8, color: T.neutral[400] }}>· {form.current_designation} at {form.current_company}</span>
                )}
              </div>
              {form.current_city && (
                <div style={{ fontSize: 12, color: T.neutral[400], marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                  <i className="ti ti-map-pin" style={{ fontSize: 12 }} />{form.current_city}
                </div>
              )}
              <div style={{ marginTop: 5, fontSize: 11, color: "#9ca3af" }}>
                <i className="ti ti-camera" style={{ fontSize: 11, marginRight: 3 }} />
                <span
                  style={{ cursor: uploadingPic ? "default" : "pointer", color: uploadingPic ? "#9ca3af" : primaryColor, fontWeight: 600 }}
                  onClick={() => !uploadingPic && picInputRef.current?.click()}
                >
                  {uploadingPic ? "Uploading…" : profilePicUrl ? "Change photo" : "Add profile photo"}
                </span>
                {!uploadingPic && <span style={{ marginLeft: 4 }}>(JPG, PNG, WebP · max 2 MB)</span>}
              </div>

            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
            <CompletionRing pct={completion} />
            <div style={{ width: 1, height: 44, background: T.neutral[200] }} />
            {!editing ? (
              <button onClick={() => setEditing(true)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", background: primaryColor, color: "#fff", border: "none", borderRadius: 9, fontSize: 13.5, fontWeight: 600, cursor: "pointer", boxShadow: `0 2px 10px ${primaryColor}42` }}>
                <i className="ti ti-edit" style={{ fontSize: 14 }} />Edit Profile
              </button>
            ) : (
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={cancelEdit} style={{ padding: "9px 16px", background: "#fff", color: T.neutral[600], border: `1px solid ${T.neutral[200]}`, borderRadius: 9, fontSize: 13.5, fontWeight: 500, cursor: "pointer" }}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 20px", background: saving ? T.neutral[300] : primaryColor, color: "#fff", border: "none", borderRadius: 9, fontSize: 13.5, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", boxShadow: saving ? "none" : `0 2px 8px ${primaryColor}40` }}>
                  {saving ? <><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", display: "inline-block", animation: "spin 0.7s linear infinite" }} />Saving…</> : <><i className="ti ti-check" style={{ fontSize: 14 }} />Save</>}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>


      {completion < 50 && (
        <div style={{ padding: "10px 16px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 9, fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 9 }}>
          <i className="ti ti-alert-triangle" style={{ color: "#f59e0b", fontSize: 16, flexShrink: 0 }} />
          Complete at least <strong style={{ margin: "0 3px" }}>50%</strong> of your profile to apply for jobs.
        </div>
      )}


      <SectionCard icon="ti-user" title="Personal Information" subtitle="Basic details and contact" accent={primaryColor}>
        <Grid>
          <div>
            <Lbl required editing={editing}>Full Name</Lbl>
            {editing ? <Inp accent={primaryColor} name="full_name" value={form.full_name} onChange={hc} placeholder="e.g. Ravi Kumar" /> : <Val>{form.full_name}</Val>}
          </div>
          <div>
            <Lbl>Email</Lbl>
            <div style={{ display: "flex", alignItems: "center", gap: 9, minHeight: 38 }}>
              <Val>{profile?.email}</Val>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: "#dcfce7", color: "#15803d", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>VERIFIED</span>
            </div>
          </div>
          <div>
            <Lbl required editing={editing}>Phone</Lbl>
            {editing ? <Inp accent={primaryColor} name="phone" value={form.phone} onChange={hc} placeholder="10-digit number" maxLength={10} type="tel" /> : <Val>{form.phone}</Val>}
          </div>
          <div>
            <Lbl>Alternate Phone</Lbl>
            {editing ? <Inp accent={primaryColor} name="alternate_phone" value={form.alternate_phone} onChange={hc} placeholder="Optional" maxLength={10} type="tel" /> : <Val>{form.alternate_phone}</Val>}
          </div>
          <div>
            <Lbl>Current City</Lbl>
            {editing ? <Inp accent={primaryColor} name="current_city" value={form.current_city} onChange={hc} placeholder="e.g. Bangalore" /> : <Val>{form.current_city}</Val>}
          </div>
          <div>
            <Lbl>Current Location</Lbl>
            {editing ? <Inp accent={primaryColor} name="current_location" value={form.current_location} onChange={hc} placeholder="e.g. Koramangala, Bangalore" /> : <Val>{form.current_location}</Val>}
          </div>
          <div>
            <Lbl>LinkedIn Profile</Lbl>
            {editing ? <Inp accent={primaryColor} name="linkedin_profile" value={form.linkedin_profile} onChange={hc} placeholder="https://linkedin.com/in/yourname" />
              : form.linkedin_profile
                ? <a href={form.linkedin_profile} target="_blank" rel="noreferrer" style={{ fontSize: T.font.body, color: primaryColor, textDecoration: "none", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 5 }}><i className="ti ti-brand-linkedin" />View Profile</a>
                : <Val />}
          </div>
          <div>
            <Lbl>Preferred Contact</Lbl>
            {editing
              ? <Sel accent={primaryColor} name="preferred_contact_method" value={form.preferred_contact_method} onChange={hc}>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="whatsapp">WhatsApp</option>
              </Sel>
              : <Val>{form.preferred_contact_method ? form.preferred_contact_method.charAt(0).toUpperCase() + form.preferred_contact_method.slice(1) : undefined}</Val>}
          </div>
        </Grid>
      </SectionCard>


      <SectionCard icon="ti-briefcase" title="Professional Details" subtitle="Employment, compensation and preferences" accent={primaryColor}>
        <Grid>
          <div>
            <Lbl required editing={editing}>Employment Status</Lbl>
            {editing
              ? <Sel accent={primaryColor} name="current_employment_status" value={form.current_employment_status} onChange={hc}>
                <option value="">Select…</option>
                {EMPLOYMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Sel>
              : <Val>{EMPLOYMENT_STATUSES.find(s => s.value === form.current_employment_status)?.label}</Val>}
          </div>
          <div>
            <Lbl>Total Experience</Lbl>
            {editing
              ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Inp accent={primaryColor} type="number" name="total_experience_years" value={form.total_experience_years} onChange={hc} min={0} max={50} step={0.5} style={{ flex: 1 }} />
                <span style={{ color: T.neutral[400], fontSize: 13, whiteSpace: "nowrap" }}>years</span>
              </div>
              : <Val>{form.total_experience_years ? `${form.total_experience_years} yrs` : undefined}</Val>}
          </div>
          <div>
            <Lbl>Current Company</Lbl>
            {editing ? <Inp accent={primaryColor} name="current_company" value={form.current_company} onChange={hc} placeholder="e.g. Infosys" /> : <Val>{form.current_company}</Val>}
          </div>
          <div>
            <Lbl>Current Designation</Lbl>
            {editing ? <Inp accent={primaryColor} name="current_designation" value={form.current_designation} onChange={hc} placeholder="e.g. Senior Developer" /> : <Val>{form.current_designation}</Val>}
          </div>
          <div>
            <Lbl>Notice Period</Lbl>
            {editing
              ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Inp accent={primaryColor} type="number" name="notice_period_days" value={form.notice_period_days} onChange={hc} min={0} max={365} placeholder="30" style={{ flex: 1 }} />
                <span style={{ color: T.neutral[400], fontSize: 13, whiteSpace: "nowrap" }}>days</span>
              </div>
              : <Val>{form.notice_period_days ? `${form.notice_period_days} days` : undefined}</Val>}
          </div>
          <div>
            <Lbl>Preferred Work Mode</Lbl>
            {editing
              ? <Sel accent={primaryColor} name="preferred_work_mode" value={form.preferred_work_mode} onChange={hc}>
                {WORK_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </Sel>
              : <Val>{WORK_MODES.find(m => m.value === form.preferred_work_mode)?.label}</Val>}
          </div>
          <div>
            <Lbl>Current CTC</Lbl>
            {editing
              ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: T.neutral[400], fontSize: 14, flexShrink: 0 }}>₹</span>
                <Inp accent={primaryColor} type="number" name="current_ctc" value={form.current_ctc} onChange={hc} min={0} placeholder="8" style={{ flex: 1 }} />
                <span style={{ color: T.neutral[400], fontSize: 13, whiteSpace: "nowrap" }}>LPA</span>
              </div>
              : <Val>{form.current_ctc ? `₹ ${form.current_ctc} LPA` : undefined}</Val>}
          </div>
          <div>
            <Lbl>Expected CTC</Lbl>
            {editing
              ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: T.neutral[400], fontSize: 14, flexShrink: 0 }}>₹</span>
                <Inp accent={primaryColor} type="number" name="expected_ctc" value={form.expected_ctc} onChange={hc} min={0} placeholder="12" style={{ flex: 1 }} />
                <span style={{ color: T.neutral[400], fontSize: 13, whiteSpace: "nowrap" }}>LPA</span>
              </div>
              : <Val>{form.expected_ctc ? `₹ ${form.expected_ctc} LPA` : undefined}</Val>}
          </div>
        </Grid>
        <Divider />
        {editing
          ? <Toggle checked={form.willing_to_relocate} onChange={v => setForm(p => ({ ...p, willing_to_relocate: v }))} label="Willing to relocate" accent={primaryColor} />
          : <div><Lbl>Willing to Relocate</Lbl>
            <Val>{form.willing_to_relocate ? <span style={{ color: "#16a34a", display: "inline-flex", alignItems: "center", gap: 5 }}><i className="ti ti-circle-check-filled" />Yes</span> : "No"}</Val>
          </div>}
      </SectionCard>


      <SectionCard icon="ti-sparkles" title="Key Skills" subtitle="Highlight your core expertise" accent={primaryColor}
        rightSlot={editing ? <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 700 }}>* Required</span> : undefined}>
        {editing && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <Inp accent={primaryColor} placeholder="Type a skill and press Enter (e.g. React, Node.js)" value={skillInput} onChange={e => setSkillInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} />
            <button type="button" onClick={addSkill} style={{ padding: "9px 16px", background: `${primaryColor}10`, color: primaryColor, border: `1.5px solid ${primaryColor}28`, borderRadius: T.radius.input, fontSize: 13.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>+ Add</button>
          </div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {keySkills.length === 0
            ? <span style={{ fontSize: 13, color: T.neutral[300] }}>No skills added yet</span>
            : keySkills.map(skill => (
              <span key={skill} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 13px", borderRadius: T.radius.badge, fontSize: 13, fontWeight: 500, background: `${primaryColor}0e`, color: primaryColor, border: `1px solid ${primaryColor}22` }}>
                {skill}
                {editing && <span onClick={() => setKeySkills(p => p.filter(s => s !== skill))} style={{ cursor: "pointer", fontSize: 16, lineHeight: 1, opacity: 0.45, fontWeight: 800 }}>×</span>}
              </span>
            ))}
        </div>
      </SectionCard>


      <SectionCard icon="ti-building" title="Work Experience" subtitle="Your professional history" accent={primaryColor}
        rightSlot={editing ? <SmBtn onClick={() => setWorkExperience(p => [...p, emptyExp()])} color={primaryColor}><i className="ti ti-plus" />Add</SmBtn> : undefined}>
        {workExperience.length === 0 && !editing && (
          <span style={{ fontSize: 13, color: T.neutral[300] }}>No work experience added</span>
        )}
        {workExperience.length === 0 && editing && (
          <div style={{ textAlign: "center", padding: "20px 0", color: T.neutral[300] }}>
            <i className="ti ti-briefcase" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />
            <div style={{ fontSize: 13 }}>Click "Add" to add your work experience</div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {workExperience.map((exp, i) => (
            <div key={i} style={{ padding: "14px 16px", borderRadius: 10, background: T.neutral[50], border: `1px solid ${T.neutral[100]}` }}>
              {editing ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
                  <div>
                    <Lbl required editing>Company</Lbl>
                    <Inp accent={primaryColor} placeholder="e.g. Google" value={exp.company}
                      onChange={e => setWorkExperience(p => p.map((x, j) => j === i ? { ...x, company: e.target.value } : x))} />
                  </div>
                  <div>
                    <Lbl required editing>Designation</Lbl>
                    <Inp accent={primaryColor} placeholder="e.g. Software Engineer" value={exp.designation}
                      onChange={e => setWorkExperience(p => p.map((x, j) => j === i ? { ...x, designation: e.target.value } : x))} />
                  </div>
                  <div>
                    <Lbl>From Year</Lbl>
                    <Inp accent={primaryColor} placeholder="2020" value={exp.from_year}
                      onChange={e => setWorkExperience(p => p.map((x, j) => j === i ? { ...x, from_year: e.target.value } : x))} />
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <Lbl>To Year</Lbl>
                      <Inp accent={primaryColor} placeholder={exp.is_current ? "Present" : "2023"} value={exp.is_current ? "" : exp.to_year} disabled={exp.is_current}
                        onChange={e => setWorkExperience(p => p.map((x, j) => j === i ? { ...x, to_year: e.target.value } : x))}
                        style={{ opacity: exp.is_current ? 0.5 : 1 }} />
                    </div>
                    <button onClick={() => setWorkExperience(p => p.filter((_, j) => j !== i))}
                      style={{ padding: "9px 12px", background: "#fff5f5", color: "#ef4444", border: "1px solid #fecaca", borderRadius: T.radius.input, cursor: "pointer", flexShrink: 0 }}>
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Toggle checked={exp.is_current} onChange={v => setWorkExperience(p => p.map((x, j) => j === i ? { ...x, is_current: v, to_year: v ? "" : x.to_year } : x))} label="Currently working here" accent={primaryColor} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Lbl>Description</Lbl>
                    <Textarea accent={primaryColor} placeholder="Brief description of your role and responsibilities…" value={exp.description || ""}
                      onChange={e => setWorkExperience(p => p.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
                  <div style={{ width: 36, height: 36, borderRadius: T.radius.icon, background: `${primaryColor}10`, display: "flex", alignItems: "center", justifyContent: "center", color: primaryColor, fontSize: 16, flexShrink: 0 }}>
                    <i className="ti ti-building" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: T.font.body, color: T.neutral[900] }}>{exp.designation}</div>
                    <div style={{ fontSize: 12.5, color: T.neutral[600], marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span>{exp.company}</span>
                      {(exp.from_year || exp.to_year) && (
                        <span style={{ color: T.neutral[400] }}>
                          · {exp.from_year} – {exp.is_current ? <span style={{ color: "#22c55e", fontWeight: 600 }}>Present</span> : exp.to_year}
                        </span>
                      )}
                    </div>
                    {exp.description && <div style={{ fontSize: 12.5, color: T.neutral[400], marginTop: 5, lineHeight: 1.55 }}>{exp.description}</div>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </SectionCard>


      <SectionCard icon="ti-school" title="Education History" subtitle="Academic qualifications" accent={primaryColor}
        rightSlot={editing ? <SmBtn onClick={() => setEducation(p => [...p, emptyEdu()])} color={primaryColor}><i className="ti ti-plus" />Add</SmBtn> : undefined}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {education.map((edu, i) => (
            <div key={i} style={{ padding: "14px 16px", borderRadius: 10, background: T.neutral[50], border: `1px solid ${T.neutral[100]}` }}>
              {editing ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
                  <div>
                    <Lbl required editing>Degree</Lbl>
                    <Inp accent={primaryColor} placeholder="e.g. B.Tech, MBA" value={edu.degree}
                      onChange={e => setEducation(p => p.map((x, j) => j === i ? { ...x, degree: e.target.value } : x))} />
                  </div>
                  <div>
                    <Lbl>Field of Study</Lbl>
                    <Inp accent={primaryColor} placeholder="e.g. Computer Science" value={edu.field || ""}
                      onChange={e => setEducation(p => p.map((x, j) => j === i ? { ...x, field: e.target.value } : x))} />
                  </div>
                  <div>
                    <Lbl required editing>Institution</Lbl>
                    <Inp accent={primaryColor} placeholder="e.g. IIT Delhi" value={edu.institution}
                      onChange={e => setEducation(p => p.map((x, j) => j === i ? { ...x, institution: e.target.value } : x))} />
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <Lbl>Year of Graduation</Lbl>
                      <Inp accent={primaryColor} placeholder="2022" value={edu.year}
                        onChange={e => setEducation(p => p.map((x, j) => j === i ? { ...x, year: e.target.value } : x))} />
                    </div>
                    {education.length > 1 && (
                      <button onClick={() => setEducation(p => p.filter((_, j) => j !== i))}
                        style={{ padding: "9px 12px", background: "#fff5f5", color: "#ef4444", border: "1px solid #fecaca", borderRadius: T.radius.input, cursor: "pointer", flexShrink: 0 }}>
                        <i className="ti ti-trash" />
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
                  <div style={{ width: 36, height: 36, borderRadius: T.radius.icon, background: `${primaryColor}10`, display: "flex", alignItems: "center", justifyContent: "center", color: primaryColor, fontSize: 16, flexShrink: 0 }}>
                    <i className="ti ti-certificate" />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: T.font.body, color: T.neutral[900] }}>{edu.degree}{edu.field ? ` in ${edu.field}` : ""}</div>
                    <div style={{ fontSize: 12.5, color: T.neutral[600], marginTop: 2 }}>{edu.institution}{edu.year && <span style={{ marginLeft: 8, color: T.neutral[400] }}>· {edu.year}</span>}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {education.length === 0 && !editing && <span style={{ fontSize: 13, color: T.neutral[300] }}>No education added</span>}
        </div>
      </SectionCard>


      <SectionCard icon="ti-language" title="Languages" subtitle="Languages you speak or write" accent={primaryColor}
        rightSlot={editing ? <SmBtn onClick={() => setLanguages(p => [...p, emptyLang()])} color={primaryColor}><i className="ti ti-plus" />Add</SmBtn> : undefined}>
        {languages.length === 0 && !editing && <span style={{ fontSize: 13, color: T.neutral[300] }}>No languages added</span>}
        {languages.length === 0 && editing && (
          <div style={{ textAlign: "center", padding: "16px 0", color: T.neutral[300] }}>
            <i className="ti ti-language" style={{ fontSize: 24, display: "block", marginBottom: 6 }} />
            <div style={{ fontSize: 13 }}>Click "Add" to add languages you know</div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {languages.map((lang, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: editing ? "1fr 1fr auto" : "1fr 1fr", gap: 12, alignItems: "center", padding: editing ? "10px 14px" : 0, background: editing ? T.neutral[50] : "transparent", borderRadius: editing ? 9 : 0, border: editing ? `1px solid ${T.neutral[100]}` : "none" }}>
              <div>
                {editing ? (
                  <Inp accent={primaryColor} placeholder="e.g. Hindi, English" value={lang.language}
                    onChange={e => setLanguages(p => p.map((x, j) => j === i ? { ...x, language: e.target.value } : x))} />
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 28, height: 28, borderRadius: 7, background: `${primaryColor}10`, display: "flex", alignItems: "center", justifyContent: "center", color: primaryColor, fontSize: 13, flexShrink: 0 }}>
                      <i className="ti ti-language" />
                    </span>
                    <span style={{ fontSize: T.font.body, fontWeight: 600, color: T.neutral[800] }}>{lang.language}</span>
                  </div>
                )}
              </div>
              <div>
                {editing ? (
                  <Sel accent={primaryColor} value={lang.proficiency}
                    onChange={e => setLanguages(p => p.map((x, j) => j === i ? { ...x, proficiency: e.target.value } : x))}>
                    {PROFICIENCY_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </Sel>
                ) : (
                  <span style={{ fontSize: 12.5, color: T.neutral[600], padding: "3px 10px", background: T.neutral[100], borderRadius: 20, display: "inline-block" }}>{lang.proficiency}</span>
                )}
              </div>
              {editing && (
                <button onClick={() => setLanguages(p => p.filter((_, j) => j !== i))}
                  style={{ padding: "8px 10px", background: "#fff5f5", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer" }}>
                  <i className="ti ti-trash" style={{ fontSize: 13 }} />
                </button>
              )}
            </div>
          ))}
        </div>
      </SectionCard>


      <SectionCard icon="ti-link" title="Portfolio & Links" subtitle="GitHub, website, Dribbble, etc." accent={primaryColor}
        rightSlot={editing ? <SmBtn onClick={() => setPortfolioLinks(p => [...p, ""])} color={primaryColor}><i className="ti ti-plus" />Add</SmBtn> : undefined}>
        {editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {portfolioLinks.map((link, i) => (
              <div key={i} style={{ display: "flex", gap: 8 }}>
                <Inp accent={primaryColor} placeholder="https://github.com/yourprofile" value={link}
                  onChange={e => setPortfolioLinks(p => p.map((l, j) => j === i ? e.target.value : l))} />
                {portfolioLinks.length > 1 && (
                  <button onClick={() => setPortfolioLinks(p => p.filter((_, j) => j !== i))}
                    style={{ padding: "9px 12px", background: "#fff5f5", color: "#ef4444", border: "1px solid #fecaca", borderRadius: T.radius.input, cursor: "pointer", flexShrink: 0 }}>
                    <i className="ti ti-trash" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {portfolioLinks.filter(l => l.trim()).length === 0
              ? <span style={{ fontSize: 13, color: T.neutral[300] }}>No links added</span>
              : portfolioLinks.filter(l => l.trim()).map((link, i) => (
                <a key={i} href={link} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: T.font.body, color: primaryColor, textDecoration: "none", fontWeight: 500 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 7, background: `${primaryColor}10`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
                    <i className={`ti ${link.includes("github") ? "ti-brand-github" : link.includes("linkedin") ? "ti-brand-linkedin" : link.includes("dribbble") ? "ti-brand-dribbble" : "ti-world"}`} />
                  </span>
                  {link}
                </a>
              ))}
          </div>
        )}
      </SectionCard>


      <SectionCard icon="ti-file-cv" title="Resume / CV" subtitle="Upload and manage your resumes" accent={primaryColor}
        rightSlot={
          <button onClick={() => resumeInputRef.current?.click()} disabled={uploadingResume}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", background: primaryColor, color: "#fff", border: "none", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: uploadingResume ? "not-allowed" : "pointer", opacity: uploadingResume ? 0.7 : 1 }}>
            {uploadingResume ? <><span style={{ width: 11, height: 11, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", display: "inline-block", animation: "spin 0.7s linear infinite" }} />Uploading…</>
              : <><i className="ti ti-upload" style={{ fontSize: 13 }} />Upload Resume</>}
          </button>
        }
      >

        <div
          onDragOver={e => { e.preventDefault(); setResumeDragOver(true); }}
          onDragLeave={() => setResumeDragOver(false)}
          onDrop={handleResumeDrop}
          onClick={() => resumeInputRef.current?.click()}
          style={{ border: `2px dashed ${resumeDragOver ? primaryColor : T.neutral[200]}`, borderRadius: 12, padding: "20px 16px", textAlign: "center", cursor: "pointer", background: resumeDragOver ? `${primaryColor}06` : T.neutral[50], transition: "all 0.2s", marginBottom: resumes.length > 0 ? 16 : 0 }}
        >
          <i className="ti ti-cloud-upload" style={{ fontSize: 28, color: resumeDragOver ? primaryColor : T.neutral[300], marginBottom: 6, display: "block" }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: resumeDragOver ? primaryColor : T.neutral[600] }}>
            {uploadingResume ? "Uploading…" : "Drop your resume here or click to browse"}
          </div>
          <div style={{ fontSize: 11.5, color: T.neutral[400], marginTop: 4 }}>PDF, DOC, DOCX, RTF, TXT · Max 5 MB</div>
        </div>


        {resumes.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {resumes.map((r: any) => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#fff", border: `1.5px solid ${r.is_primary ? `${primaryColor}40` : T.neutral[200]}`, borderRadius: 10, transition: "all 0.15s" }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: r.is_primary ? `${primaryColor}12` : T.neutral[100], display: "flex", alignItems: "center", justifyContent: "center", color: r.is_primary ? primaryColor : T.neutral[400], fontSize: 17, flexShrink: 0 }}>
                  <i className="ti ti-file-text" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.neutral[900], display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.file_name}</span>
                    {r.is_primary && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 20, background: `${primaryColor}14`, color: primaryColor, border: `1px solid ${primaryColor}30`, whiteSpace: "nowrap", flexShrink: 0 }}>PRIMARY</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: T.neutral[400], marginTop: 2 }}>
                    {r.file_size ? fmtFileSize(r.file_size) : ""}{r.uploaded_at ? ` · Uploaded ${new Date(r.uploaded_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                  </div>
                </div>
                <a href={r.resume_url} target="_blank" rel="noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: primaryColor, fontWeight: 600, textDecoration: "none", padding: "5px 10px", background: `${primaryColor}0d`, borderRadius: 7, border: `1px solid ${primaryColor}20`, whiteSpace: "nowrap" }}>
                  <i className="ti ti-eye" style={{ fontSize: 12 }} />View
                </a>
              </div>
            ))}
          </div>
        )}

        {resumes.length === 0 && (
          <div style={{ fontSize: 13, color: T.neutral[300], textAlign: "center", marginTop: 4 }}>No resumes uploaded yet</div>
        )}
      </SectionCard>


      {editing && (
        <div style={{ position: "sticky", bottom: 0, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(10px)", borderTop: `1px solid ${T.neutral[200]}`, padding: "12px 4px", display: "flex", justifyContent: "flex-end", gap: 10, zIndex: 10, marginTop: 4 }}>
          <button onClick={cancelEdit} style={{ padding: "9px 22px", background: "#fff", color: T.neutral[600], border: `1px solid ${T.neutral[200]}`, borderRadius: 9, fontSize: 14, fontWeight: 500, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 26px", background: saving ? T.neutral[300] : primaryColor, color: "#fff", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", boxShadow: saving ? "none" : `0 2px 12px ${primaryColor}40` }}>
            {saving ? <><span style={{ width: 13, height: 13, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", display: "inline-block", animation: "spin 0.7s linear infinite" }} />Saving…</> : "Save Profile"}
          </button>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ProfileTab;